import axios from 'axios';
import { useSnackbar } from 'notistack5';
import { Dispatch, useCallback } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePendingRespondentApi } from 'src/api/usePendingRespondentApi';
import { useEnqueteCreateGrid } from 'src/features/general/enquete-create/hooks/useEnqueteCreateGrid';
import { FormValues } from '../components/BulkCreateEmailModal';
import { extractEmailsBeforeSeparator, replaceEmailsBeforeSeparator } from '../utils/emailParser';

type UseRegisterUnregisteredUserButtonProps = {
  respondentRequestId: string | undefined;
  errorMessage: string;
  setIsLoading: Dispatch<boolean>;
  setErrorMessage: Dispatch<string>;
  setValue: UseFormSetValue<FormValues>;
  getValues: UseFormGetValues<FormValues>;
};

export const useRegisterUnregisteredUserButton = ({
  respondentRequestId,
  errorMessage,
  setIsLoading,
  setErrorMessage,
  setValue,
  getValues,
}: UseRegisterUnregisteredUserButtonProps) => {
  const { postPendingRespondent } = usePendingRespondentApi();
  const { getRequestAnswerList } = useEnqueteCreateGrid();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const divider = t(`enqueteCreate.bulkCreateEmailModal.error.mailAddressOrganizationRetrievalError`);
  const storageKey = 'BulkCreateEmailModal';
  const errMessageKey = 'ErrMessage';
  const enterEmailsKey = 'EnterEmails';

  // NOTE: パフォーマンスが問題になるような場合はメモ化する
  const errorEmails: string[] = extractEmailsBeforeSeparator(errorMessage, divider);

  const handleOnClick = useCallback(async () => {
    if (!respondentRequestId) return;
    setIsLoading(true);
    const emails: string[] = getValues('emails').split('\n');
    try {
      const responseData = await postPendingRespondent(respondentRequestId, errorEmails);
      if (responseData) {
        const updatedErrorEmails = responseData?.result.filter((result) => result.status === 'error').map((result) => result.email);
        const successEmails = responseData?.result.filter((result) => result.status === 'success').map((result) => result.email);

        if (updatedErrorEmails.length > 0) {
          enqueueSnackbar(t('enqueteCreate.bulkCreateEmailModal.button.RegisterUnregisteredUser.message.partialError'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('enqueteCreate.bulkCreateEmailModal.button.RegisterUnregisteredUser.message.success'), { variant: 'success' });
        }
        const updatedErrorMessage = replaceEmailsBeforeSeparator(errorMessage, updatedErrorEmails, divider);
        const updatedFormState = emails.filter((email) => !successEmails.includes(email));
        setValue('emails', updatedFormState.join('\n'));
        setErrorMessage(updatedErrorMessage);
        localStorage.setItem(`${storageKey}${errMessageKey}${respondentRequestId}`, JSON.stringify(updatedErrorMessage));
        localStorage.setItem(`${storageKey}${enterEmailsKey}${respondentRequestId}`, JSON.stringify(updatedFormState));
      }
    } catch (error) {
      // NOTE: axiosエラー以外の時は500番ページの表示
      if (!axios.isAxiosError(error)) {
        throw error;
      }
    } finally {
      await getRequestAnswerList(respondentRequestId);
      setIsLoading(false);
    }
  }, [respondentRequestId, errorMessage, divider, setIsLoading, setErrorMessage]);

  return { handleOnClick, errorEmails };
};
